import React, { useEffect, useRef } from 'react';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

// Destination images - high quality
const IMAGES = {
  korea: "https://images.unsplash.com/photo-1517154421773-0529f29ea451?w=800&q=80",
  japan: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80",
  spain: "https://images.unsplash.com/photo-1722863380905-539ae092fc5f?w=800&q=80"
};

const SpecializationSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const destinations = [
    { key: 'korea', image: IMAGES.korea, ...t.specialization.korea },
    { key: 'japan', image: IMAGES.japan, ...t.specialization.japan },
    { key: 'spain', image: IMAGES.spain, ...t.specialization.spain },
  ];

  return (
    <section 
      id="specialization" 
      ref={sectionRef}
      className="py-32 md:py-40 bg-black relative"
      data-testid="specialization-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-20 md:mb-28 animate-on-scroll">
          <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-zinc-500 mb-4" data-testid="spec-overline">
            {t.specialization.overline}
          </p>
          <h2 
            className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight text-white mb-6"
            data-testid="spec-title"
          >
            {t.specialization.title}
          </h2>
          <p 
            className="text-zinc-500 text-base md:text-lg max-w-2xl mx-auto font-light"
            data-testid="spec-subtitle"
          >
            {t.specialization.subtitle}
          </p>
          <div className="w-16 h-px bg-white/20 mx-auto mt-8" />
        </div>

        {/* Destinations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {destinations.map((dest, index) => (
            <div 
              key={dest.key}
              className="animate-on-scroll"
              style={{ transitionDelay: `${index * 0.15}s` }}
            >
              <div 
                className="group relative h-[450px] md:h-[550px] overflow-hidden border border-white/5 hover:border-white/20 transition-all duration-700"
                data-testid={`destination-${dest.key}`}
              >
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img 
                    src={dest.image} 
                    alt={dest.title}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 filter grayscale group-hover:grayscale-0"
                  />
                  {/* Dark overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
                </div>

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10">
                  {/* Number */}
                  <span className="text-white/10 font-serif text-8xl absolute top-6 right-8">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  
                  <h3 className="font-serif text-3xl md:text-4xl font-normal text-white mb-4">
                    {dest.title}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed mb-6 opacity-0 group-hover:opacity-100 transition-all duration-500 transform translate-y-4 group-hover:translate-y-0">
                    {dest.description}
                  </p>
                  <div className="flex items-center gap-2 text-white/60 text-xs tracking-[0.15em] uppercase opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100">
                    <span>Explore</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </div>

                {/* Top line accent */}
                <div className="absolute top-0 left-0 w-full h-px bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-700 origin-left" />
              </div>
            </div>
          ))}
        </div>

        {/* Flow Indicator */}
        <div className="mt-20 flex items-center justify-center gap-6 text-zinc-600 animate-on-scroll">
          <span className="text-xs tracking-[0.2em] uppercase">Europe</span>
          <div className="flex items-center gap-3">
            <div className="w-12 h-px bg-white/10" />
            <div className="flex gap-1">
              <ArrowRight className="w-4 h-4 text-white/30" />
              <ArrowRight className="w-4 h-4 text-white/30 rotate-180" />
            </div>
            <div className="w-12 h-px bg-white/10" />
          </div>
          <span className="text-xs tracking-[0.2em] uppercase">Asia</span>
        </div>
      </div>
    </section>
  );
};

export default SpecializationSection;
